import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Assessment, AssessmentType, CreateAssessmentDTO, UpdateAssessmentDTO } from '@promo/shared';
import { get, post, put, deleteFn } from '@/lib/api';
import { useToast } from '@/hooks/use-toast';

/**
 * Hook for BAR_RAISER assessments
 */
export function useBarRaiserAssessments(packetId?: string, enabled: boolean = true) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const queryKey = ['assessments', packetId, 'bar-raiser'];

  // Fetch assessments
  const listQuery = useQuery<Assessment[], Error>({
    queryKey,
    queryFn: () => (packetId ? get<Assessment[]>('/assessments', { packetId, type: AssessmentType.BAR_RAISER }) : Promise.resolve([])),
    enabled: !!packetId && enabled,
  });

  // Mutations
  const createMutation = useMutation<Assessment, Error, CreateAssessmentDTO>({
    mutationFn: (data) => post<Assessment>('/assessments', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: 'Saved', description: 'Assessment submitted' });
    },
    onError: (error) => toast({ title: 'Error', description: error.message, variant: 'destructive' })
  });

  const updateMutation = useMutation<Assessment, Error, { id: string; data: UpdateAssessmentDTO }>({
    mutationFn: ({ id, data }) => put<Assessment>(`/assessments/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: 'Updated', description: 'Assessment updated' });
    },
    onError: (error) => toast({ title: 'Error', description: error.message, variant: 'destructive' })
  });

  const deleteMutation = useMutation<void, Error, string>({
    mutationFn: (id) => deleteFn<void>(`/assessments/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey });
      toast({ title: 'Deleted', description: 'Assessment removed' });
    },
    onError: (error) => toast({ title: 'Error', description: error.message, variant: 'destructive' })
  });

  return {
    list: listQuery.data || [],
    isLoading: listQuery.isLoading,
    error: listQuery.error,
    createAssessment: createMutation.mutate,
    updateAssessment: (id: string, data: UpdateAssessmentDTO) => updateMutation.mutate({ id, data }),
    deleteAssessment: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
} 